import React from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

const SONAX_HEADERS = [
  "IDENTIFICADOR",
  "NUMERO DE TELEFONE 1",
  "NUMERO DE TELEFONE 2",
  "NUMERO DE TELEFONE 3",
  "NUMERO DE TELEFONE 4",
  "NUMERO DE TELEFONE 5",
  "CAMPO EXTRA 1",
  "CAMPO EXTRA 2",
  "CAMPO EXTRA 3",
  "CAMPO EXTRA 4",
  "CAMPO EXTRA 5",
];

const SONAX_MAX_FILE_SIZE = 10 * 1024 * 1024;

// Função utilitária para buscar o nome pelo id
function getNomeById(id, lista, campo = "nome") {
  if (!id || !Array.isArray(lista)) return "";
  const item = lista.find((i) => String(i.id) === String(id));
  if (!item) return id;
  return (
    item[campo] ||
    item.nome_fantasia ||
    item.profissional ||
    item.regiao ||
    id
  );
}

// A Sonax recebe o telefone no formato nacional: DDD + número, sem +55
// e sem o zero usado antes do DDD.
function normalizarTelefoneSonax(valor) {
  let telefone = String(valor || "").replace(/\D/g, "");

  if ((telefone.length === 12 || telefone.length === 13) && telefone.startsWith("55")) {
    telefone = telefone.slice(2);
  }

  if (
    (telefone.length === 11 || telefone.length === 12) &&
    telefone.startsWith("0") &&
    (telefone.length - 1 === 10 || telefone.length - 1 === 11)
  ) {
    telefone = telefone.slice(1);
  }

  return /^\d{10,11}$/.test(telefone) ? telefone : "";
}

function limparCampoSonax(valor) {
  return String(valor ?? "")
    .replace(/\r?\n|\r/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function escaparCampoCsvSonax(valor) {
  const texto = limparCampoSonax(valor);

  if (/[;\"]/.test(texto)) {
    return `"${texto.replace(/"/g, '""')}"`;
  }

  return texto;
}

// O arquivo de exemplo da Sonax usa ISO-8859-1. Esta conversão preserva
// os acentos do português e substitui apenas símbolos fora dessa codificação.
function converterParaIso88591(texto) {
  const bytes = new Uint8Array(texto.length);

  for (let i = 0; i < texto.length; i += 1) {
    const codigo = texto.charCodeAt(i);
    bytes[i] = codigo <= 255 ? codigo : 63;
  }

  return bytes;
}

export default function LeadsExportExcel({
  rows,
  columns,
  procedimentos,
  dentistas,
  regioes,
  usuarios,
  fontes,
  campanhas, // Adicione esta prop ao usar o componente!
  selectedLeadIds,
}) {
  const idsSelecionados = new Set(
    Array.from(selectedLeadIds || []).map((id) => String(id)),
  );
  const rowsDisponiveis = Array.isArray(rows) ? rows : [];
  const rowsSelecionadas = rowsDisponiveis.filter((row) =>
    idsSelecionados.has(String(row.original?.id ?? "")),
  );
  const rowsParaExportar =
    rowsSelecionadas.length > 0 ? rowsSelecionadas : rowsDisponiveis;
  const exportandoSelecao = rowsSelecionadas.length > 0;

  function exportarLeadsExcel() {
    if (rowsParaExportar.length === 0) {
      toast.warning("Nenhuma lead disponível para exportação.");
      return;
    }

    const colunasExportaveis = columns.filter(
      col =>
        ![
          "acoes",
          "interacoes",
          "tarefas",
          "bolinha_status",
          "selecao_exportacao",
        ].includes(col.id || col.accessor)
        && typeof (col.accessor || col.id) !== "undefined"
    );

    const data = rowsParaExportar.map(row => {
      const lead = row.original;
      const obj = {};
      colunasExportaveis.forEach(col => {
        let header = col.Header;
        if (typeof header === "function") {
          try {
            const fake = header({});
            header = fake?.props?.children || fake || col.id || col.accessor;
          } catch {
            header = col.id || col.accessor;
          }
        }
        if (typeof header === "object" && header !== null && "props" in header) {
          header = header.props.children || col.id || col.accessor;
        }

        // Processa ID para nome amigável
        switch (col.id) {
          case "id_procedimento":
            obj[header] = getNomeById(lead.id_procedimento, procedimentos);
            break;
          case "id_regiao":
            obj[header] = getNomeById(lead.id_regiao, regioes, "regiao");
            break;
          case "usuario_criador_id":
            obj[header] = getNomeById(lead.usuario_criador_id, usuarios);
            break;
          case "id_dentista":
            obj[header] = getNomeById(lead.id_dentista, dentistas, "profissional");
            break;
          case "id_fonte_lead":
            obj[header] = getNomeById(lead.id_fonte_lead, fontes);
            break;
          case "nome_campanha":
          case "id_campanha": {
            // Sempre exporta o nome da campanha se possível
            let nome = lead.nome_campanha || "";
            if ((!nome || nome === "Sem Campanha") && lead.id_campanha && Array.isArray(campanhas)) {
              const camp = campanhas.find(c => String(c.id) === String(lead.id_campanha));
              if (camp) nome = camp.nome;
            }
            obj[header] = nome || "Sem Campanha";
            break;
          }
          case "total_orcamento":
          case "total_contratado":
            obj[header] = Number(lead[col.id] || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
            break;
          case "data_agendamento":
          case "data_lead":
          case "criado_em":
            obj[header] = lead[col.id] ? ("" + lead[col.id]).slice(0, 10).split("-").reverse().join("/") : "";
            break;
          default:
            obj[header] = lead[col.id] || lead[col.accessor] || "";
        }
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      blob,
      `${exportandoSelecao ? "leads_selecionadas" : "leads"}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );

    toast.success(
      `${rowsParaExportar.length} lead(s) exportada(s) para o Excel.`,
    );
  }

  function exportarLeadsSonax() {
    const linhas = [];
    let ignoradas = 0;

    rowsParaExportar.forEach((row) => {
      const lead = row.original || {};
      const identificador = String(lead.id ?? "").trim();

      const telefones = [
        lead.telefone1,
        lead.telefone2,
        lead.telefone3,
        lead.telefone4,
        lead.telefone5,
      ]
        .map(normalizarTelefoneSonax)
        .filter(Boolean)
        .filter((telefone, indice, lista) => lista.indexOf(telefone) === indice)
        .slice(0, 5);

      if (!/^\d+$/.test(identificador) || telefones.length === 0) {
        ignoradas += 1;
        return;
      }

      const procedimento =
        lead.procedimento ||
        lead.nome_procedimento ||
        lead.procedimento_nome ||
        getNomeById(lead.id_procedimento, procedimentos);

      const captador =
        lead.captador ||
        lead.nome_captador ||
        getNomeById(lead.usuario_criador_id, usuarios);

      const valores = [
        identificador,
        ...telefones,
        ...Array(5 - telefones.length).fill(""),
        lead.nome_lead || "",
        procedimento || "",
        captador || "",
        lead.observacao_paciente || "",
        lead.status_negociacao || "",
      ];

      linhas.push(valores.map(escaparCampoCsvSonax).join(";"));
    });

    if (linhas.length === 0) {
      toast.warning(
        "Nenhuma lead com identificador e telefone válidos para exportar à Sonax.",
      );
      return;
    }

    const conteudoCsv = `${SONAX_HEADERS.join(";")}\r\n${linhas.join("\r\n")}\r\n`;
    const conteudoIso = converterParaIso88591(conteudoCsv);

    if (conteudoIso.byteLength > SONAX_MAX_FILE_SIZE) {
      toast.error(
        "O mailing ultrapassou o limite de 10 MB da Sonax. Aplique mais filtros e tente novamente.",
      );
      return;
    }

    const blob = new Blob([conteudoIso], {
      type: "text/csv;charset=iso-8859-1",
    });

    saveAs(
      blob,
      `mailing_sonax_${new Date().toISOString().slice(0, 10)}.csv`,
    );

    const resumoIgnoradas = ignoradas > 0 ? ` ${ignoradas} ignorada(s).` : "";
    toast.success(
      `${linhas.length} lead(s) exportada(s) para a Sonax.${resumoIgnoradas}`,
    );
  }

  return (
    <>
      <button
        onClick={exportarLeadsExcel}
        className="flex items-center justify-center gap-1 w-[130px] h-8 bg-green-200 hover:bg-green-300 text-green-800 border-2 border-green-500 rounded-xl text-[11px] shadow-md font-bold transition-all duration-200"
        style={{ minWidth: 0, minHeight: 0, lineHeight: 1 }}
        type="button"
        title={
          exportandoSelecao
            ? `Exportar ${rowsSelecionadas.length} lead(s) selecionada(s) para o Excel`
            : "Exportar todas as leads filtradas para o Excel"
        }
      >
        <span role="img" aria-label="excel" style={{ fontSize: 12 }}>
          📁
        </span>
        Exportar Excel
      </button>

      <button
        onClick={exportarLeadsSonax}
        className="flex items-center justify-center gap-1 w-[130px] h-8 bg-sky-200 hover:bg-sky-300 text-sky-800 border-2 border-sky-500 rounded-xl text-[11px] shadow-md font-bold transition-all duration-200"
        style={{ minWidth: 0, minHeight: 0, lineHeight: 1 }}
        type="button"
        title={
          exportandoSelecao
            ? `Exportar ${rowsSelecionadas.length} lead(s) selecionada(s) para a Sonax`
            : "Exportar todas as leads filtradas para a discadora Sonax"
        }
      >
        <span role="img" aria-label="telefone" style={{ fontSize: 12 }}>
          📞
        </span>
        Exportar Sonax
      </button>
    </>
  );
}
